import { useCallback } from 'react';
import { PageFeed, User } from 'model/indexTS';
import { useTranslation } from 'utils/translation';
import { useAuthSelectors } from 'services/auth';
import { PageState, usePageSelectors } from 'services/page';
import { usePageFeedActions, usePageFeedSelectors } from 'services/page-feed';
import { useReactAlert } from 'components/react-alert-provider';
import { usePermissionsSelectors } from 'services/permissions';
import { useUserSelectors } from 'services/user';

export interface UseFeedListOutput {
  handleDeleteClick: (feedId: string) => void;
  handleLikeClick: (feed: PageFeed) => void;
  isDeleting: boolean | undefined;
  isUserAuthenticated: boolean;
  page: PageState;
  isStaff: boolean;
  user: User | null;
  feeds?: PageFeed[] | null;
}

export const useFeedList = (): UseFeedListOutput => {
  const { deletePageFeed, likePageFeed } = usePageFeedActions();
  const { showSuccess, showError } = useReactAlert();
  const { isDeleting, data } = usePageFeedSelectors();
  const { isUserAuthenticated } = useAuthSelectors();
  const { isStaff } = usePermissionsSelectors();
  const { page } = usePageSelectors();
  const { t } = useTranslation('pageFeed');
  const { user } = useUserSelectors();

  const handleDeleteClick = useCallback(
    async (feedId: string) => {
      const result = await deletePageFeed(page.slug, feedId);
      if (result.error) {
        showError(t('pageFeed:feedDetail.delete.deleteError'));
        return;
      }

      showSuccess(t('pageFeed:feedDetail.delete.deleteSuccess'));
    },
    [deletePageFeed, page.slug, showError, showSuccess, t],
  );

  const handleLikeClick = useCallback(
    async (feed: PageFeed) => {
      const response = await likePageFeed(page.slug, feed._id, { liked: !feed.summary.liked });
      if (response.error) {
        showError(t('pageFeed:feedDetail.summary.error'));
      }
    },
    [likePageFeed, page.slug, showError, t],
  );

  return {
    handleDeleteClick,
    handleLikeClick,
    isDeleting,
    isUserAuthenticated,
    page,
    isStaff,
    user,
    feeds: data,
  };
};
